import { Link } from 'react-router-dom';
import type { PortfolioBacktest } from '@einhaus/project314-types';
import { FrozenStrategiesCell } from './PortfolioBacktestFrozenCapital';
import type { FrozenSnapshotDrift } from './portfolioBacktestFrozenSnapshot';

export interface PortfolioBacktestIdentityPortfolio {
    id: number;
    /** Undefined while the portfolio list has not resolved this id. */
    name: string | undefined;
}

interface PortfolioBacktestIdentityCellProps {
    backtest: PortfolioBacktest;
    /** Null when every visible row shares one portfolio, where repeating it on each row adds nothing. */
    portfolio: PortfolioBacktestIdentityPortfolio | null;
    drift: FrozenSnapshotDrift;
    exitCandidateLabelMap: ReadonlyMap<number, string>;
    optionBuyingStrategyNameMap: ReadonlyMap<number, string>;
    strategyNameMap: ReadonlyMap<number, string>;
}

/**
 * A run's identity in one pinned cell: its name as the headline, then the portfolio and strategies
 * that produced it. Stacking them keeps the long, prefix-family names readable in a single column
 * narrow enough to leave the stat columns in view.
 */
const PortfolioBacktestIdentityCell = ({
    backtest,
    portfolio,
    drift,
    exitCandidateLabelMap,
    optionBuyingStrategyNameMap,
    strategyNameMap
}: PortfolioBacktestIdentityCellProps) => {
    const portfolioLine = portfolio
        ? { path: `/user/portfolio/${portfolio.id.toString()}`, label: portfolio.name ?? `Portfolio ${portfolio.id.toString()}` }
        : null;

    return (
        <div className="flex min-w-0 flex-col gap-1">
            {/*
             * Run names are prefix-families that differ only in their tail ("… + Positions to 5" vs
             * "… + Positions to 5 + Compounding"), so the name clamps to two lines instead of truncating
             * and hiding the one distinguishing token. The tooltip covers anything longer.
             */}
            <span className="line-clamp-2 text-sm font-medium text-zinc-50" title={backtest.name}>
                {backtest.name}
            </span>
            {backtest.taxConfiguration && (
                <Link className="text-[11px] text-emerald-300" to={`/user/portfolioBacktestStats?id=${backtest.id}`}>
                    Tax comparison: {backtest.taxConfiguration.federalRatePercent}% federal + {backtest.taxConfiguration.stateRatePercent}%
                    state · table shows before taxes
                </Link>
            )}

            {portfolioLine && (
                // Labelled because portfolio names share prefixes with the strategy names below, so a bare
                // link would read as another strategy.
                <div className="truncate text-[11px] text-zinc-500" title={`Portfolio: ${portfolioLine.label}`}>
                    Portfolio{' '}
                    <Link className="text-zinc-300 hover:text-blue-300" to={portfolioLine.path}>
                        {portfolioLine.label}
                    </Link>
                </div>
            )}

            <FrozenStrategiesCell
                backtest={backtest}
                drift={drift}
                exitCandidateLabelMap={exitCandidateLabelMap}
                optionBuyingStrategyNameMap={optionBuyingStrategyNameMap}
                strategyNameMap={strategyNameMap}
            />
        </div>
    );
};

export default PortfolioBacktestIdentityCell;
